import React, { useState, useEffect, useRef } from 'react'
import { Flipper, Flipped } from 'react-flip-toolkit'

import { usePageEditorContext } from '../../contexts/page-editor.context'

import { InlineEditor } from './inline-editor'
import { TableToolbar } from './table-toolbar'
import { textTableService } from '../../blocks/table/text-table.service'
import { useTableStyles } from '../../blocks/table/text-table.style'
import { useStyles } from './text-table-edit-inline.style'

export const TextTableEditInline = function ({ block }) {
  const tableClasses = useTableStyles()
  const classes = useStyles()

  const [table, setTable] = useState()
  const [currentDataCell, setCurrentDataCell] = useState()
  const [currentHeaderCell, setCurrentHeaderCell] = useState()

  const pageEditorContext = usePageEditorContext() || {}
  const tableRef = useRef()

  useEffect(() => {
    if (!block.props.header) {
      block.props.header = textTableService.initializeHeader()
    }

    if (!block.props.data) {
      block.props.data = textTableService.initializeDataRows()
    }

    const transformedData = textTableService.transformBlockDataToLayout(block.props)
    setTable(transformedData)
  }, [])

  function onHeaderCellTextChange (cell) {
    const cellIndex = textTableService.getHeaderCellLocation(table, cell.key)
    block.props.header[cellIndex].delta = cell.delta
    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.setCurrentEdit({ block, isValid: true })
  }

  function onCellTextChange (cell) {
    const { row, column } = textTableService.getDataCellLocation(table, cell.key)
    block.props.data[row][column].delta = cell.delta
    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.setCurrentEdit({ block, isValid: true })
  }

  function onHeaderCellSelectionChange (cell, isFirstSelection) {
    if (isFirstSelection) {
      setCurrentHeaderCell(cell)
      setCurrentDataCell()
    }
  }

  function onCellSelectionChange (cell, isFirstSelection) {
    if (isFirstSelection) {
      setCurrentDataCell(cell)
      setCurrentHeaderCell()
    }
  }

  function handleOnDelete () {
    setCurrentHeaderCell()
    setCurrentDataCell()
  }

  function isSelectedCell (cell) {
    return (cell.key === (currentHeaderCell && currentHeaderCell.key)) || (cell.key === (currentDataCell && currentDataCell.key))
  }

  function onDrag (event, cell) {
    const width = getDraggedWidth(event)
    const headerCell = getHeaderCellToResize(cell)
    headerCell.style.width = `${width}px`
  }

  function onDragEnd (event, cell) {
    const headerCell = getHeaderCellToResize(cell)

    // Use the actual width of the column, since only the cell itself knows the real width.
    const actualHeaderWidth = window.getComputedStyle(headerCell).width
    headerCell.style.width = actualHeaderWidth

    // Save width
    const cellIndex = getColumnIndexByKey(cell.key)
    block.props.header[cellIndex].width = actualHeaderWidth
    table.header[cellIndex].width = actualHeaderWidth
  }

  function getDraggedWidth (event) {
    let width = 0
    let node = event.currentTarget.offsetParent

    while (node) {
      width += node.offsetLeft
      node = node.offsetParent
    }

    return event.clientX - width
  }

  function getHeaderCellToResize (cell) {
    const cellIndex = getColumnIndexByKey(cell.key)
    const { key } = table.header[cellIndex]
    return document.getElementById(key)
  }

  function getColumnIndexByKey (key) {
    let cellIndex = textTableService.getHeaderCellLocation(table, key)
    if (cellIndex === -1) {
      const { column } = textTableService.getDataCellLocation(table, key)
      cellIndex = column
    }
    return cellIndex
  }

  if (!table) {
    return null
  }

  const headerKeys = table.header.reduce((acc, cell) => (acc + cell.key), '')
  const cellKeys = table.data.reduce((acc, { row }) => {
    return acc + row.reduce((acc, cell) => (acc + cell.key), '')
  }, '')
  const flipKey = headerKeys + cellKeys

  return (
    <>
      <TableToolbar block={block} table={table} setTable={setTable} currentDataCell={currentDataCell} currentHeaderCell={currentHeaderCell} onDelete={handleOnDelete} />
      <Flipper flipKey={flipKey}>
        <div className={classes.table} ref={tableRef}>
          <div className={tableClasses.header}>
            {table.header && table.header.map((cell, index) => (
              <Flipped key={cell.key} flipId={cell.key}>
                <div id={cell.key} className={tableClasses.headerCell} style={{ width: cell.width }}>
                  <InlineEditor cell={cell}
                    showHighlight={isSelectedCell(cell)}
                    isInHeaderCell={true}
                    onSelectionChange={onHeaderCellSelectionChange}
                    onTextChange={onHeaderCellTextChange} />
                  {(index < (table.header.length - 1)) &&
                    <div className={classes.resizeGrabber} draggable="true"
                      onDrag={(event) => { onDrag(event, cell) }}
                      onDragEnd={(event) => { onDragEnd(event, cell) }} />
                  }
                </div>
              </Flipped>
            ))}
          </div>
          {table.data && table.data.map(({ key, row }) => (
            <div key={key} className={tableClasses.row}>
              {row.map((cell, colIndex) => (
                <Flipped key={cell.key} flipId={cell.key}>
                  <div className={tableClasses.dataCell}>
                    <InlineEditor cell={cell}
                      showHighlight={isSelectedCell(cell)}
                      isInHeaderCell={false}
                      onSelectionChange={onCellSelectionChange}
                      onTextChange={onCellTextChange} />
                    {(colIndex < (table.header.length - 1)) &&
                      <div className={classes.resizeGrabber} draggable="true"
                        onDrag={(event) => { onDrag(event, cell) }}
                        onDragEnd={(event) => { onDragEnd(event, cell) }} />
                    }
                  </div>
                </Flipped>
              ))}
            </div>
          ))}
        </div>
      </Flipper>
    </>
  )
}
